<template>
  <h3 v-if="titleTranslationExists" class="emh-side-filters-content-header__title wb-type-heading-s">
    {{ $t('dcp.sideFilters.title') }}
  </h3>
  <testing-generic-wrapper storage-key="SelectedFiltersPill">
    <template #control>
      <button
        v-if="isAnyFilterSelected && showResetAll"
        class="emh-side-filters-content-header__reset-button wb-button wb-button--tertiary wb-button--small"
        data-test-id="emh-side-filters-content-header__reset-button"
        :disabled="isLoading"
        @click="onClearAllFiltersTracking"
      >
        <wb-icon class="wb-button__icon" :name.attr="'close'" aria-hidden="true" flip-rtl></wb-icon>
        {{ $t('dcp.assortment.cars.filtersSearch.filters.reset') }} ({{ selectedFiltersCount }})
      </button>
    </template>
  </testing-generic-wrapper>
  <emh-side-filters-categories-toggle
    v-if="availableCategories.length > 1 && !!selectedCategory"
    class="emh-side-filters-content-header__categories-toggle"
    :is-loading="isLoading"
    :available-categories="availableCategories"
    :selected-category="selectedCategory"
    data-test-id="emh-side-filters-content-header__categories-toggle"
    @change-category="onChangeCategory"
  />
  <testing-generic-wrapper storage-key="AvailabilityCategory">
    <template #target>
      <emh-side-filters-availability-category
        :availability-filter-category="availabilityFilter"
        :disabled="isLoading"
        @change-filter="onChangeFilter"
      />
    </template>
  </testing-generic-wrapper>
</template>
<script setup lang="ts">
import { computed, PropType, toRefs } from 'vue';
import { useI18n } from '@dh-io-emh/shared/src/composables/i18n.composable';
import { useSideFiltersContent } from './side-filters-content.composable';
import { FilterCategory } from '@dh-io-emh/config-types';
import { IProductFilterGroup } from '@dh-io-emh/shared/src/models/product-filter/product-filter-base.model';
import { IProductFilterType } from '@dh-io-emh/shared/src/models/product-filter/product-filter.model';
import EmhSideFiltersAvailabilityCategory from '../sideFiltersCategories/sideFiltersAvailabilityCategory/sideFiltersAvailabilityCategory.vue';
import EmhSideFiltersCategoriesToggle from '../sideFiltersCategoriesToggle/SideFiltersCategoriesToggle.vue';
import TestingGenericWrapper from '@dh-io-emh/shared/src/components/testingGenericWrapper/TestingGenericWrapper.vue';

const props = defineProps({
  isLoading: {
    required: true,
    type: Boolean,
  },
  showResetAll: {
    required: false,
    type: Boolean,
    default: () => true,
  },
  selectedCategory: {
    required: true,
    type: String,
  },
  availableCategories: {
    required: true,
    type: Array as PropType<FilterCategory[]>,
  },
  sideFilterCategories: {
    required: true,
    type: Array as PropType<IProductFilterGroup[]>,
  },
  selectedVisibleFilters: {
    required: true,
    type: Array as PropType<IProductFilterType[]>,
  },
});

const emit = defineEmits(['clear-all-filters', 'change-filter', 'change-category']);

const { sideFilterCategories, selectedVisibleFilters } = toRefs(props);

const { exists } = useI18n();
const titleTranslationExists = computed(() => exists('dcp.sideFilters.title'));
const { isAnyFilterSelected, selectedFiltersCount, availabilityFilter } = useSideFiltersContent(
  sideFilterCategories,
  selectedVisibleFilters,
);

const onClearAllFiltersTracking = () => {
  emit('clear-all-filters');
};

const onChangeCategory = (category) => {
  emit('change-category', category);
};

const onChangeFilter = (filter) => {
  emit('change-filter', filter);
};
</script>
<style lang="scss">
.emh-side-filters-content-header {
  &__title,
  &__categories-toggle {
    @include wb-spacing('margin-bottom', 'm');
  }

  &__title {
    align-items: center;
    display: flex;
  }

  &__reset-button {
    @include wb-spacing('margin-bottom', 's');

    @include breakpoint-to(mq4) {
      display: none;
    }
  }

  &__reset-all-filters {
    @include wb-spacing('margin-top', 'xxs');
  }
}
</style>
